/**
 * MessageQuotaChip — Mini contador ambient en el header.
 *
 * Diseno:
 *   - Mode 'trial' (Primer Mes Gratis): chip emerald "26/30" + label corto.
 *   - Mode 'daily' (Plan Gratis): chip ambar "4/6" si quedan, rojo si 0/6.
 *   - Mode 'premium': no se renderiza (Premium ilimitado).
 *   - Mientras carga: nada (evita flash en el header).
 *
 * Click → CTA Premium (`navigateToPremium`).
 *
 * Visible siempre que el usuario este logueado y no sea Premium. El chip
 * acompana al usuario en cada accion (ambient awareness) lo cual mejora la
 * conversion vs banners que solo aparecen al limite.
 */
import React from 'react';
import { Sparkles } from 'lucide-react';
import { useUserLimits } from '../state/userLimits';
import { useLanguage } from '../context/LanguageContext';
import { navigateToPremium } from '../utils/navigateToPremium';

export const MessageQuotaChip = () => {
  const { language } = useLanguage();
  const limits = useUserLimits();
  const isEs = language === 'es';

  if (limits.loading) return null;
  // 🟥 P26.35 — si el plan es DESCONOCIDO (fetch fallido/401 transitorio) no
  // mostramos nada: antes se pintaba "0/8 Hoy" a cuentas Premium/creador.
  if (!limits.plan_known) return null;
  if (limits.is_premium) return null;
  if (limits.mode === 'premium') return null;

  let used, max, palette, label;
  if (limits.mode === 'trial') {
    used = limits.trial_messages_used;
    max = limits.trial_messages_max;
    palette = 'border-emerald-500/40 bg-emerald-950/40 text-emerald-200 hover:bg-emerald-900/50';
    label = isEs ? 'Mes Gratis' : 'Free Month';
  } else {
    used = limits.daily_messages_used;
    max = limits.daily_messages_max;
    const exhausted = limits.daily_messages_remaining <= 0;
    palette = exhausted
      ? 'border-rose-500/50 bg-rose-950/40 text-rose-200 hover:bg-rose-900/50'
      : 'border-amber-500/40 bg-amber-950/40 text-amber-200 hover:bg-amber-900/50';
    label = isEs ? 'Hoy' : 'Today';
  }

  const titleAttr = limits.mode === 'trial'
    ? (isEs
        ? `Llevas ${used} de ${max} mensajes del Primer Mes Gratis`
        : `${used} of ${max} messages used in your First Free Month`)
    : (isEs
        ? `Quedan ${limits.daily_messages_remaining} de ${max} mensajes hoy`
        : `${limits.daily_messages_remaining} of ${max} messages left today`);

  return (
    <button
      type="button"
      onClick={navigateToPremium}
      data-testid="message-quota-chip"
      data-mode={limits.mode}
      title={titleAttr}
      aria-label={titleAttr}
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold border transition-colors cursor-pointer ${palette}`}
    >
      <Sparkles className="w-3 h-3" />
      <span className="tabular-nums">{used}/{max}</span>
      <span className="opacity-75">{label}</span>
    </button>
  );
};

export default MessageQuotaChip;
